import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  ListSubheader,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import {
  createV2DebugProfile,
  decideV2ApprovalStream,
  deleteV2DebugProfile,
  executeV2FlowDebug,
  executeV2FlowStream,
  getV2Asset,
  listV2Approvals,
  listV2Assets,
  listV2DebugProfiles,
  queueV2AssetProcessing,
  uploadV2Asset,
} from '../../../api/studioV2.api';
import StudioV2FlowDebugPanel, { studioV2LastRunStorageKey } from '../StudioV2FlowDebugPanel';
import { getStudioV2NodeMeta } from '../studioV2NodeCatalog';
import { buildTraceFailureSuggestions } from '../../../utils/studioV2TraceRollup';
import {
  buildRunTimeline, formatSeconds, humanizeKey, inputFormShape, readAt, stepCopy, writeAt,
} from './flowBuilderModel';
import RunResultView from './RunResultView';
import ToolCallList from './ToolCallList';
import ApprovalCard from './ApprovalCard';
import {
  aiStepProblems, fallbackNote, providerLabel, useAiStatus, usesLocalModel,
} from './aiModels';

const INPUT_STORAGE = (flowId) => `studio_flow_test_input_v1:${flowId}`;

function defaultPayload(def) {
  const st = def?.state;
  if (st && typeof st === 'object' && !Array.isArray(st) && Object.keys(st).length) return st;
  return { input: 'Hello' };
}

function readStoredPayload(flowId) {
  try {
    const raw = window.localStorage.getItem(INPUT_STORAGE(flowId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function tintFor(theme, type) {
  const key = getStudioV2NodeMeta(type).colorKey;
  if (key === 'grey') return theme.palette.grey[600];
  return theme.palette[key]?.main || theme.palette.primary.main;
}

function InputForm({
  payload, shape, onChange, disabled,
}) {
  if (shape.kind === 'message') {
    return (
      <TextField
        label="Message"
        multiline
        minRows={3}
        fullWidth
        disabled={disabled}
        value={readAt(payload, shape.path) ?? ''}
        onChange={(e) => onChange(writeAt(payload, shape.path, e.target.value))}
      />
    );
  }
  const base = readAt(payload, shape.path) || {};
  return (
    <Stack spacing={1.5}>
      {shape.fields.map((key) => {
        const value = base[key];
        const label = humanizeKey(key);
        const set = (v) => onChange(writeAt(payload, [...shape.path, key], v));
        if (typeof value === 'boolean') {
          return (
            <FormControlLabel
              key={key}
              disabled={disabled}
              control={<Switch size="small" checked={value} onChange={(e) => set(e.target.checked)} />}
              label={label}
            />
          );
        }
        if (typeof value === 'number') {
          return (
            <TextField
              key={key}
              label={label}
              type="number"
              size="small"
              fullWidth
              disabled={disabled}
              value={value}
              onChange={(e) => set(e.target.value === '' ? 0 : Number(e.target.value))}
            />
          );
        }
        const text = value == null ? '' : String(value);
        return (
          <TextField
            key={key}
            label={label}
            size="small"
            fullWidth
            disabled={disabled}
            multiline={text.length > 60}
            minRows={text.length > 60 ? 2 : undefined}
            placeholder={value == null ? 'empty' : undefined}
            value={text}
            onChange={(e) => set(value == null && e.target.value === '' ? null : e.target.value)}
          />
        );
      })}
    </Stack>
  );
}

function StepRow({ row, theme }) {
  const [open, setOpen] = useState(false);
  const meta = getStudioV2NodeMeta(row.type);
  const copy = stepCopy(row.type, meta);
  const Icon = meta.Icon;
  const tint = tintFor(theme, row.type);
  const toolCalls = row.toolCalls || [];
  const hasDetail = Boolean(row.error || row.text || toolCalls.length || row.fallback);
  const statusIcon = {
    running: <CircularProgress size={18} thickness={5} />,
    done: <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />,
    failed: <ErrorIcon sx={{ fontSize: 20, color: 'error.main' }} />,
    stopped: <RemoveCircleOutlineIcon sx={{ fontSize: 20, color: 'text.disabled' }} />,
    waiting: <HourglassTopIcon sx={{ fontSize: 20, color: 'warning.main' }} />,
  }[row.status];
  const liveText = row.status === 'running' && row.text ? row.text.slice(-240) : '';

  return (
    <Box data-testid="flow-test-step" data-status={row.status} sx={{ borderBottom: 1, borderColor: 'divider', '&:last-of-type': { borderBottom: 0 } }}>
      <ButtonBase
        onClick={() => hasDetail && setOpen((o) => !o)}
        disableRipple={!hasDetail}
        sx={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1.25, textAlign: 'left', cursor: hasDetail ? 'pointer' : 'default',
        }}
      >
        <Box sx={{ width: 22, display: 'grid', placeItems: 'center' }}>{statusIcon}</Box>
        <Box sx={{
          width: 32, height: 32, borderRadius: 1.5, flexShrink: 0, display: 'grid', placeItems: 'center', bgcolor: alpha(tint, 0.12), color: tint,
        }}
        >
          <Icon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>{row.label}</Typography>
          <Typography variant="caption" color="text.secondary" component="div" noWrap>
            {copy.name}
            {row.model ? ` · ${providerLabel(row.model.provider)}${row.model.model ? ` (${row.model.model})` : ''}` : ''}
            {row.fallback ? ' · switched to Gemini' : ''}
            {row.decision ? ` · went to “${row.decision}”` : ''}
            {toolCalls.length ? ` · called ${toolCalls.length} tool${toolCalls.length > 1 ? 's' : ''}` : ''}
            {row.status === 'failed' ? ' · failed' : ''}
            {row.status === 'waiting' ? ' · waiting for a person' : ''}
          </Typography>
        </Box>
        {row.runs > 1 && <Chip size="small" variant="outlined" label={`ran ${row.runs}×`} />}
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 48, textAlign: 'right' }}>
          {row.status === 'running' ? 'running' : row.status === 'waiting' ? '' : formatSeconds(row.duration)}
        </Typography>
        {hasDetail && (
          <ExpandMoreIcon sx={{
            fontSize: 18, color: 'text.disabled', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s',
          }}
          />
        )}
      </ButtonBase>
      {liveText && !open && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block', px: 1.5, pb: 1, pl: 9.5, fontStyle: 'italic', whiteSpace: 'pre-wrap',
          }}
        >
          …
          {liveText}
        </Typography>
      )}
      <Collapse in={open}>
        <Box sx={{ px: 1.5, pb: 1.5, pl: 9.5 }}>
          {row.fallback && (
            <Alert severity="info" sx={{ mb: 1 }} data-testid="flow-test-fallback">
              {fallbackNote(row.fallback)}
            </Alert>
          )}
          {row.error && <Alert severity="error" sx={{ mb: row.text ? 1 : 0 }}>{String(row.error)}</Alert>}
          {toolCalls.length > 0 && <ToolCallList calls={toolCalls} />}
          {row.text && (
            <Box sx={{
              bgcolor: 'action.hover', borderRadius: 1.5, p: 1.25, maxHeight: 240, overflow: 'auto',
            }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 700, mb: 0.5 }}>What the AI wrote</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{row.text}</Typography>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

/**
 * Test tab: choose or type sample input, run the saved flow, and watch each step finish with a readable result.
 */
export default function FlowTestPanel({
  flowId, def, definitionText, onBeforeRun, onNotify, initialProfileId = '',
}) {
  const theme = useTheme();
  const aiStatus = useAiStatus();
  const aiProblems = useMemo(() => aiStepProblems(def, aiStatus), [def, aiStatus]);
  const localModel = useMemo(() => Boolean(aiStatus) && usesLocalModel(def, aiStatus), [def, aiStatus]);
  const [payload, setPayload] = useState(() => readStoredPayload(flowId) ?? defaultPayload(def));
  const [jsonText, setJsonText] = useState(() => JSON.stringify(payload, null, 2));
  const [jsonError, setJsonError] = useState(null);
  const [mode, setMode] = useState(() => (inputFormShape(payload).kind === 'json' ? 'json' : 'form'));
  const [profiles, setProfiles] = useState([]);
  const [profilesError, setProfilesError] = useState(null);
  const [profileId, setProfileId] = useState('');
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [busy, setBusy] = useState(false);
  const [events, setEvents] = useState([]);
  const [requestError, setRequestError] = useState(null);
  const [showRaw, setShowRaw] = useState(false);
  const [stepThrough, setStepThrough] = useState(false);
  const [detailsKey, setDetailsKey] = useState(0);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [assets, setAssets] = useState([]);
  const [assetIds, setAssetIds] = useState([]);
  const [assetBusy, setAssetBusy] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [approvalError, setApprovalError] = useState(null);
  const abortRef = useRef(null);
  const collectedRef = useRef([]);
  const fileRef = useRef(null);
  const touchedInputRef = useRef(Boolean(readStoredPayload(flowId)));

  const shape = useMemo(() => inputFormShape(payload), [payload]);
  const timeline = useMemo(() => buildRunTimeline(events, def), [events, def]);
  const suggestions = useMemo(
    () => (timeline.status === 'failed' ? buildTraceFailureSuggestions(events).slice(0, 3) : []),
    [timeline.status, events],
  );

  const applyPayload = (next, { touched = true } = {}) => {
    setPayload(next);
    setJsonText(JSON.stringify(next, null, 2));
    setJsonError(null);
    if (touched) touchedInputRef.current = true;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [{ profiles: rows, error }, { assets: listed }] = await Promise.all([listV2DebugProfiles(), listV2Assets()]);
      if (cancelled) return;
      setProfilesError(error || null);
      setProfiles(rows || []);
      setAssets(listed || []);
      const preselect = initialProfileId && (rows || []).find((p) => String(p._id) === String(initialProfileId));
      const mine = (rows || []).find((p) => String(p?.payload?.flowId || '') === String(flowId));
      const pick = preselect || (!touchedInputRef.current ? mine : null);
      if (pick) {
        setProfileId(String(pick._id));
        const input = pick.payload?.input;
        if (input != null && typeof input === 'object') applyPayload(input, { touched: false });
      }
    })();
    return () => { cancelled = true; };
  }, [flowId, initialProfileId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    try {
      window.localStorage.setItem(INPUT_STORAGE(flowId), JSON.stringify(payload));
    } catch {
      /* storage full or disabled */
    }
  }, [flowId, payload]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const refreshApprovals = async () => {
    const { approvals } = await listV2Approvals({ flowId });
    setPendingApprovals(approvals);
  };
  useEffect(() => { refreshApprovals(); }, [flowId]); // eslint-disable-line react-hooks/exhaustive-deps

  const watchedAssets = assets.filter((a) => assetIds.includes(String(a._id)) && ['queued', 'processing'].includes(String(a.processingStatus || '')));
  useEffect(() => {
    if (!watchedAssets.length) return undefined;
    const timer = setInterval(async () => {
      const updates = await Promise.all(watchedAssets.map((a) => getV2Asset(String(a._id))));
      setAssets((prev) => prev.map((a) => updates.find((u) => String(u.asset?._id) === String(a._id))?.asset || a));
    }, 3000);
    return () => clearInterval(timer);
  }, [watchedAssets.map((a) => a._id).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const flowProfiles = profiles.filter((p) => String(p?.payload?.flowId || '') === String(flowId));
  const otherProfiles = profiles.filter((p) => String(p?.payload?.flowId || '') !== String(flowId));

  const onPickProfile = (value) => {
    setProfileId(value);
    if (!value) return;
    const p = profiles.find((x) => String(x._id) === String(value));
    const input = p?.payload?.input;
    if (input != null && typeof input === 'object') applyPayload(input);
  };

  const onJsonChange = (value) => {
    setJsonText(value);
    try {
      const parsed = JSON.parse(value);
      setPayload(parsed);
      setJsonError(null);
      touchedInputRef.current = true;
    } catch (e) {
      setJsonError(e.message);
    }
  };

  const saveRun = (next, extra = {}) => {
    const executionId = next.find((e) => e?.execution_id)?.execution_id || extra.executionId || null;
    const done = next.find((e) => e?.type === 'flow_complete');
    try {
      sessionStorage.setItem(studioV2LastRunStorageKey(flowId), JSON.stringify({
        t: Date.now(),
        executionId,
        source: extra.source || 'stream',
        data: extra.data || (done ? { ...(done.data || {}), events: next.slice(-120) } : { events: next.slice(-120), eventCount: next.length }),
      }));
    } catch {
      /* quota */
    }
  };

  const onRun = async () => {
    if (jsonError) return;
    setRequestError(null);
    setShowRaw(false);
    if (onBeforeRun) {
      const ok = await onBeforeRun();
      if (!ok) {
        setRequestError('Your latest changes could not be saved, so the test would run an older version. Fix the save problem first.');
        return;
      }
    }
    setBusy(true);
    setEvents([]);
    const opts = assetIds.length ? { assetIds } : {};
    if (stepThrough) {
      const { result, error } = await executeV2FlowDebug(flowId, payload, { ...opts, pauseOnEveryNode: true });
      setBusy(false);
      if (error) {
        setRequestError(error);
        return;
      }
      saveRun([], { source: 'debug', data: result, executionId: result?.execution_id ?? result?.executionId });
      setDetailsKey((k) => k + 1);
      setDetailsOpen(true);
      onNotify?.('Step-by-step run started. Use the debugger below to move through each step.');
      return;
    }
    await streamRun([], (onEvent, signal) => executeV2FlowStream(flowId, payload, onEvent, { signal, ...opts }));
  };

  /** Runs one stream, appending its events after `start` (a paused run continues its own timeline). */
  const streamRun = async (start, open, { onError = setRequestError } = {}) => {
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const collected = [...start];
    collectedRef.current = collected;
    setEvents([...collected]);
    try {
      await open((ev) => {
        collected.push(ev);
        if (collected.length % 25 === 0 || ev?.type !== 'llm_token') setEvents([...collected]);
      }, ctrl.signal);
      setEvents([...collected]);
    } catch (e) {
      if (e?.name !== 'AbortError') onError(e?.message || String(e));
      else collected.push({ type: 'flow_error', error: 'You stopped this run.' });
      setEvents([...collected]);
    } finally {
      abortRef.current = null;
      setBusy(false);
      saveRun(collected);
      setDetailsKey((k) => k + 1);
      refreshApprovals();
    }
  };

  const onDecide = async (approval, decision) => {
    setApprovalError(null);
    setRequestError(null);
    setBusy(true);
    const current = timeline.approval?.id === approval.id ? collectedRef.current : [];
    await streamRun(
      current,
      (onEvent, signal) => decideV2ApprovalStream(approval.id, decision, onEvent, { signal }),
      { onError: setApprovalError },
    );
  };

  const onStop = () => abortRef.current?.abort();

  const onSaveProfile = async () => {
    const name = saveName.trim();
    if (!name) return;
    const { profile, error } = await createV2DebugProfile(name, { flowId, input: payload });
    if (error) {
      setRequestError(error);
      return;
    }
    setSaveOpen(false);
    setSaveName('');
    setProfiles((prev) => [profile, ...prev.filter((x) => String(x._id) !== String(profile._id))]);
    if (profile?._id) setProfileId(String(profile._id));
    onNotify?.('Test case saved');
  };

  const onDeleteProfile = async () => {
    const p = profiles.find((x) => String(x._id) === String(profileId));
    if (!p || !window.confirm(`Delete the test case "${p.name}"?`)) return;
    const { error } = await deleteV2DebugProfile(profileId);
    if (error) {
      setRequestError(error);
      return;
    }
    setProfiles((prev) => prev.filter((x) => String(x._id) !== String(profileId)));
    setProfileId('');
    onNotify?.('Test case deleted');
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setAssetBusy(true);
    for (const file of files) {
      const kind = ['image', 'audio', 'video'].find((k) => file.type.startsWith(`${k}/`));
      const { asset, error } = await uploadV2Asset(file, { kind });
      if (error) {
        setRequestError(error);
        continue;
      }
      if (asset?._id) {
        const aid = String(asset._id);
        const { asset: queued } = await queueV2AssetProcessing(aid);
        setAssets((prev) => [queued?._id ? queued : asset, ...prev.filter((x) => String(x._id) !== aid)]);
        setAssetIds((prev) => (prev.includes(aid) ? prev : [...prev, aid]));
      }
    }
    setAssetBusy(false);
  };

  const { status, rows, progress } = timeline;
  const total = progress?.total || (def?.nodes?.length ?? 0);
  const doneCount = rows.filter((r) => r.status === 'done').length;

  return (
    <Box sx={{
      display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(360px, 5fr) 7fr' }, gap: 3, alignItems: 'start',
    }}
    >
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3 }}>
        <Typography variant="subtitle1" fontWeight={700}>Test input</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The data the flow receives when it starts, just like an app would send it.
        </Typography>
        {profilesError && <Alert severity="warning" sx={{ mb: 2 }}>Could not load saved test cases: {profilesError}</Alert>}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <TextField
            select
            size="small"
            fullWidth
            label="Test case"
            value={profileId}
            onChange={(e) => onPickProfile(e.target.value)}
            disabled={busy}
            SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 360 } } } }}
            data-testid="flow-test-case"
          >
            <MenuItem value=""><em>Custom input</em></MenuItem>
            {flowProfiles.length > 0 && <ListSubheader>Saved for this flow</ListSubheader>}
            {flowProfiles.map((p) => <MenuItem key={p._id} value={String(p._id)}>{p.name}</MenuItem>)}
            {otherProfiles.length > 0 && <ListSubheader>Other saved inputs</ListSubheader>}
            {otherProfiles.map((p) => <MenuItem key={p._id} value={String(p._id)}>{p.name}</MenuItem>)}
          </TextField>
          <Tooltip title="Save this input as a test case">
            <span>
              <IconButton onClick={() => setSaveOpen(true)} disabled={busy || Boolean(jsonError)} aria-label="Save as test case">
                <BookmarkAddOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
          {profileId && (
            <Tooltip title="Delete this test case">
              <IconButton onClick={onDeleteProfile} disabled={busy} aria-label="Delete test case">
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="body2" fontWeight={600}>
            {mode === 'form' ? 'Fields' : 'JSON'}
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={mode}
            onChange={(_e, v) => v && setMode(v)}
          >
            <ToggleButton value="form" disabled={shape.kind === 'json'} sx={{ textTransform: 'none', px: 1.5 }}>Form</ToggleButton>
            <ToggleButton value="json" sx={{ textTransform: 'none', px: 1.5 }}>JSON</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        {mode === 'form' && shape.kind !== 'json' ? (
          <InputForm payload={payload} shape={shape} onChange={applyPayload} disabled={busy} />
        ) : (
          <TextField
            value={jsonText}
            onChange={(e) => onJsonChange(e.target.value)}
            multiline
            minRows={8}
            maxRows={20}
            fullWidth
            disabled={busy}
            error={Boolean(jsonError)}
            helperText={jsonError ? `This is not valid JSON yet: ${jsonError}` : 'Any JSON object.'}
            InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
            inputProps={{ 'data-testid': 'flow-test-json' }}
          />
        )}
        <Stack direction="row" spacing={1} sx={{ mt: 2.5 }}>
          {busy && !stepThrough ? (
            <Button variant="outlined" color="inherit" startIcon={<StopIcon />} onClick={onStop} sx={{ textTransform: 'none', fontWeight: 600 }}>
              Stop
            </Button>
          ) : (
            <Button
              variant="contained"
              size="large"
              startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
              onClick={onRun}
              disabled={busy || Boolean(jsonError)}
              sx={{ textTransform: 'none', fontWeight: 700, px: 3 }}
              data-testid="flow-test-run"
            >
              {status === 'idle' ? 'Run test' : 'Run again'}
            </Button>
          )}
        </Stack>
        {aiProblems.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }} data-testid="flow-test-ai-problem">
            {aiProblems.length === 1
              ? `"${aiProblems[0].label}" will fail: ${aiProblems[0].message}`
              : `${aiProblems.length} AI steps will fail because their model is not available.`}
            {' '}
            Change the AI model on the step in Build.
          </Alert>
        )}
        {!aiProblems.length && localModel && (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5 }} data-testid="flow-test-local-note">
            AI steps here run on this server&apos;s own model, which can take a minute or more per step.
          </Typography>
        )}
        <Accordion
          disableGutters
          elevation={0}
          sx={{
            mt: 2.5, border: 1, borderColor: 'divider', borderRadius: 2, '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" fontWeight={600}>More options</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1.5}>
              <FormControlLabel
                control={<Switch size="small" checked={stepThrough} onChange={(e) => setStepThrough(e.target.checked)} disabled={busy} />}
                label={(
                  <Box>
                    <Typography variant="body2">Go step by step</Typography>
                    <Typography variant="caption" color="text.secondary">Pause before every step so you can inspect values.</Typography>
                  </Box>
                )}
                sx={{ alignItems: 'flex-start' }}
              />
              <Box>
                <input ref={fileRef} type="file" hidden multiple onChange={onFiles} />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={assetBusy ? <CircularProgress size={14} /> : <AttachFileIcon />}
                  onClick={() => fileRef.current?.click()}
                  disabled={assetBusy || busy}
                  sx={{ textTransform: 'none' }}
                >
                  Attach files
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  For flows that read documents, images or audio.
                </Typography>
                {assets.length > 0 && (
                  <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ mt: 1 }}>
                    {assets.slice(0, 8).map((a) => {
                      const aid = String(a._id);
                      const on = assetIds.includes(aid);
                      const pending = ['queued', 'processing'].includes(String(a.processingStatus || ''));
                      return (
                        <Chip
                          key={aid}
                          size="small"
                          color={on ? 'primary' : 'default'}
                          variant={on ? 'filled' : 'outlined'}
                          label={`${a.originalName || a.kind || 'file'}${pending ? ' · processing' : ''}`}
                          onClick={() => setAssetIds((prev) => (on ? prev.filter((x) => x !== aid) : [...prev, aid]))}
                        />
                      );
                    })}
                  </Stack>
                )}
              </Box>
              <Button
                size="small"
                onClick={() => setDetailsOpen((o) => !o)}
                sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
              >
                {detailsOpen ? 'Hide the detailed debugger' : 'Open the detailed debugger'}
              </Button>
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Paper>

      <Stack spacing={2} sx={{ minWidth: 0 }}>
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }} data-testid="flow-test-results">
          <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>Steps</Typography>
              {status === 'running' && (
                <Typography variant="body2" color="text.secondary">
                  {total ? `${Math.min(doneCount, total)} of ${total} done` : 'Running…'}
                </Typography>
              )}
              {status === 'done' && (
                <Chip color="success" size="small" icon={<CheckCircleIcon />} label={`Finished in ${formatSeconds(timeline.totalTime) || 'no time'}`} data-testid="flow-test-status" />
              )}
              {status === 'failed' && <Chip color="error" size="small" icon={<ErrorIcon />} label="Did not finish" data-testid="flow-test-status" />}
              {status === 'waiting' && <Chip color="warning" size="small" icon={<HourglassTopIcon />} label="Waiting for a person" data-testid="flow-test-status" />}
            </Stack>
          </Box>
          {status === 'running' && (
            <LinearProgress
              variant={progress?.percentage != null ? 'determinate' : 'indeterminate'}
              value={Math.min(100, progress?.percentage ?? 0)}
            />
          )}
          {requestError && <Alert severity="error" sx={{ mx: 2, mb: 2 }}>{requestError}</Alert>}
          {status === 'idle' && !requestError && (
            <Stack alignItems="center" spacing={1} sx={{ py: 6, px: 3, textAlign: 'center' }}>
              <ScienceOutlinedIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360 }}>
                {busy ? 'Starting the run…' : 'Run a test to watch each step happen here, then see the final result.'}
              </Typography>
            </Stack>
          )}
          {rows.length > 0 && (
            <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
              {rows.map((row) => <StepRow key={row.id} row={row} theme={theme} />)}
            </Box>
          )}
        </Paper>

        {status === 'waiting' && timeline.approval && (
          <Box data-testid="flow-run-approval">
            <ApprovalCard
              key={timeline.approval.id}
              approval={timeline.approval}
              busy={busy}
              error={approvalError}
              onDecide={(d) => onDecide(timeline.approval, d)}
            />
          </Box>
        )}

        {!busy && pendingApprovals.filter((a) => a.id !== timeline.approval?.id).length > 0 && (
          <Box data-testid="flow-pending-approvals">
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Other runs waiting for a person</Typography>
            <Stack spacing={1.5}>
              {pendingApprovals.filter((a) => a.id !== timeline.approval?.id).slice(0, 5).map((a) => (
                <ApprovalCard key={a.id} approval={a} busy={busy} onDecide={(d) => onDecide(a, d)} />
              ))}
            </Stack>
          </Box>
        )}

        {status === 'failed' && (
          <Alert severity="error" data-testid="flow-test-error">
            <Typography variant="body2" fontWeight={700}>{String(timeline.error || 'The run failed')}</Typography>
            {suggestions.map((s) => (
              <Typography key={s.title} variant="body2" sx={{ mt: 0.75 }}>
                <strong>{s.title}.</strong>
                {' '}
                {s.detail}
              </Typography>
            ))}
          </Alert>
        )}

        {status === 'done' && (
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }} data-testid="flow-test-result">
            <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>Result</Typography>
              <FormControlLabel
                control={<Switch size="small" checked={showRaw} onChange={(e) => setShowRaw(e.target.checked)} />}
                label={<Typography variant="body2" color="text.secondary">Show raw data</Typography>}
                sx={{ mr: 0 }}
              />
            </Stack>
            {showRaw ? (
              <Box sx={{
                bgcolor: 'action.hover', borderRadius: 1.5, p: 1.5, maxHeight: 480, overflow: 'auto',
              }}
              >
                <Typography component="pre" variant="caption" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {JSON.stringify(timeline.rawResult, null, 2)}
                </Typography>
              </Box>
            ) : (
              timeline.result == null
                ? <Typography variant="body2" color="text.secondary">The flow finished without returning anything.</Typography>
                : <RunResultView value={timeline.result} />
            )}
          </Paper>
        )}

        {detailsOpen && (
          <StudioV2FlowDebugPanel
            key={detailsKey}
            flowMongoId={flowId}
            definitionText={definitionText}
            autoOpenDebug={stepThrough}
          />
        )}
      </Stack>

      <Dialog open={saveOpen} onClose={() => setSaveOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Save test case</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            placeholder="For example: Senior candidate, needs visa"
            fullWidth
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') onSaveProfile(); }}
          />
          <Typography variant="caption" color="text.secondary">
            Saves the current input so you can rerun it any time.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={onSaveProfile} disabled={!saveName.trim()}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
